<script>
import { reactive, onMounted, ref } from 'vue'
import indicadorTreinador from './indicadorTreinador.vue'
import indicadorAluno from './indicadorAluno.vue'
import indicadorTreinos from './indicadorTreinos.vue'
import indicadorIA from './indicadorIA.vue'
import { useStore } from './../../store'
import dashboardRepository from '@/services/api/dashboardRepository'

export default {
  components: {
    indicadorTreinador,
    indicadorAluno,
    indicadorTreinos,
    indicadorIA,
  },
  setup() {
    const authStore = useStore()
    const loading = ref(true)

    const state = reactive({
      indicadores: {
        totalCoaches: { total: 0, M: 0, F: 0 },
        totalAlunos: { total: 0, M: 0, F: 0 },
        totalTreinos: 0,
        listaCoaches: [],
        listaAlunos: [],
        ia: null,
      },
    })

    function buscaIndicadores() {
      loading.value = true
      dashboardRepository
        .Indicadores()
        .then((response) => {
          if (response.data.success) {
            state.indicadores = { ...state.indicadores, ...response.data.data }
          }
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          loading.value = false
        })
    }

    function iniciais(nome) {
      if (!nome) return '?'
      const p = nome.trim().split(/\s+/)
      return (p.length === 1
        ? p[0].substring(0, 2)
        : p[0][0] + p[p.length - 1][0]
      ).toUpperCase()
    }

    onMounted(() => {
      buscaIndicadores()
    })

    return { state, loading, authStore, iniciais, recarregar: buscaIndicadores }
  },
}
</script>

<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h3 class="mb-0">Dashboard</h3>
        <small class="text-muted">Visão geral da plataforma</small>
      </div>
      <button class="btn btn-sm btn-outline-primary d-flex align-items-center"
              @click="recarregar" :disabled="loading">
        <i class="material-icons me-1" :class="{ girando: loading }" style="font-size: 18px;">refresh</i>
        Atualizar
      </button>
    </div>

    <!-- KPIs principais -->
    <div class="row g-3">
      <div class="col-12 col-xl-3 col-lg-6 col-md-6">
        <indicador-treinador :indicador="state.indicadores['totalCoaches']" />
      </div>
      <div class="col-12 col-xl-3 col-lg-6 col-md-6">
        <indicador-aluno :indicador="state.indicadores['totalAlunos']" />
      </div>
      <div class="col-12 col-lg-6 col-md-6 col-xl-3">
        <indicador-treinos :total="state.indicadores['totalTreinos']" />
      </div>
      <div class="col-12 col-lg-6 col-md-6 col-xl-3">
        <indicador-i-a :indicador="state.indicadores['ia']" :total-treinos="state.indicadores['totalTreinos']" />
      </div>
    </div>

    <!-- Listas de treinadores e alunos -->
    <div class="row g-3 mt-0">
      <div class="col-12 col-lg-6">
        <div class="card h-100">
          <div class="card-header pb-2">
            <h5 class="mb-0">
              <i class="material-icons align-middle me-1 text-primary">groups</i>
              Últimos treinadores cadastrados
            </h5>
          </div>
          <div class="card-body p-0">
            <div v-if="!state.indicadores.listaCoaches || state.indicadores.listaCoaches.length === 0"
                 class="text-center text-muted small py-4">
              Nenhum treinador cadastrado ainda.
            </div>
            <ul v-else class="list-group list-group-flush">
              <li v-for="(item, index) in state.indicadores['listaCoaches']" :key="index"
                  class="list-group-item d-flex align-items-center">
                <div class="avatar me-3 avatar-azul text-white">
                  {{ iniciais(item['name']) }}
                </div>
                <div class="flex-1">
                  <div class="fw-semibold">{{ item['name'] }}</div>
                  <small class="text-muted">{{ item['city'] || '—' }} / {{ item['state'] || '—' }}</small>
                </div>
                <span class="badge bg-success">Ativo</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-6">
        <div class="card h-100">
          <div class="card-header pb-2">
            <h5 class="mb-0">
              <i class="material-icons align-middle me-1 text-success">school</i>
              Últimos alunos cadastrados
            </h5>
          </div>
          <div class="card-body p-0">
            <div v-if="!state.indicadores.listaAlunos || state.indicadores.listaAlunos.length === 0"
                 class="text-center text-muted small py-4">
              Nenhum aluno cadastrado ainda.
            </div>
            <ul v-else class="list-group list-group-flush">
              <li v-for="(item, index) in state.indicadores['listaAlunos']" :key="index"
                  class="list-group-item d-flex align-items-center">
                <div class="avatar me-3 avatar-verde text-white">
                  {{ iniciais(item['name']) }}
                </div>
                <div class="flex-1">
                  <div class="fw-semibold">{{ item['name'] }}</div>
                  <small class="text-muted">{{ item['city'] || '—' }} / {{ item['state'] || '—' }}</small>
                </div>
                <span class="badge bg-success">Ativo</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.girando { animation: giro 1s linear infinite; }
@keyframes giro { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.82rem;
  flex-shrink: 0;
}
.avatar-azul {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
}
.avatar-verde {
  background: linear-gradient(135deg, #22c55e 0%, #15803d 100%);
}
.flex-1 { flex: 1; min-width: 0; }
</style>
